import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/shared/api/rest/client'
import { ENDPOINTS } from '@/shared/api/rest/config'
import { ParentRecord } from '@/shared/api/rest/types'
import { RegistryParent } from '../types'

// `GET /co-parents/:parentId` returns the co-parent relationship with embedded
// `parent`/`coParent` records (now enriched with name/email/phone/picture).
// The spouse is whichever embedded record isn't the queried parent.
type EmbeddedParent = ParentRecord & { email?: string | null }

type CoParentRelationship = {
    parentId?: string | null
    coParentId?: string | null
    parent?: EmbeddedParent | null
    coParent?: EmbeddedParent | null
} | null

const resolveSpouse = (
    relationship: CoParentRelationship,
    parentId: string
): RegistryParent | null => {
    if (!relationship) return null
    const candidates = [relationship.parent, relationship.coParent].filter(
        (record): record is EmbeddedParent => Boolean(record)
    )
    return (candidates.find((record) => record._id !== parentId) ??
        null) as RegistryParent | null
}

/**
 * Fetch a parent's linked co-parent / spouse (`GET /co-parents/:parentId`) and
 * return the enriched spouse record. Returns null when the parent has no
 * co-parent (the endpoint 404s).
 */
export const useParentCoParent = (parentId?: string) => {
    const query = useQuery({
        queryKey: ['parent-coparent', parentId],
        queryFn: () =>
            api
                .get<CoParentRelationship>(
                    ENDPOINTS.CO_PARENTS.BY_PARENT_ID(parentId as string)
                )
                .catch((err: unknown) => {
                    // A 404 means the parent simply has no co-parent — treat that as
                    // "no data" rather than an error. Any other failure (network,
                    // 5xx, etc.) should surface via `query.error` as normal.
                    if (err instanceof ApiError && err.status === 404) return null
                    throw err
                }),
        enabled: Boolean(parentId),
        staleTime: 5 * 60 * 1000
    })

    return {
        coParent: parentId ? resolveSpouse(query.data ?? null, parentId) : null,
        loading: query.isLoading,
        error: query.error
    }
}
